/**
 * Segment (analytics.js) identity helpers.
 *
 * Kept free of store imports so both `./index.tsx` (AnalyticsProvider) and `./reset.ts`
 * (called from the auth slice) can share the single dedupe flag without an import cycle.
 *
 * Loading model: `public/index.html` installs a snippet stub on `window.analytics` that only
 * queues method calls. When analytics.js finishes loading it replays that queue and then
 * REPLACES `window.analytics` with the real instance (which is the only object exposing
 * `user()`). Anything that must read the anonymous id therefore has to re-read the global
 * inside `ready()`, never through a reference captured before load.
 */
export type AnalyticsProperties = Record<string, unknown>;

export interface SegmentClient {
  ready?: (callback: () => void) => unknown;
  user?: () => { anonymousId?: () => string | null | undefined } | undefined;
  alias?: (...args: unknown[]) => unknown;
  identify?: (userId: string, traits?: AnalyticsProperties) => unknown;
  group?: (groupId: string, traits?: AnalyticsProperties) => unknown;
  reset?: () => unknown;
}

/**
 * Normalises an anonymous id handed over in a URL (`?anonymous_id=`). Senders may forward the
 * raw `ajs_anonymous_id` localStorage value, which is JSON-encoded (wrapped in double quotes).
 * Returns '' when nothing usable is left, so callers can skip the alias.
 */
export const normalizeAnonymousId = (raw?: string | null) => (raw ?? '').trim().replace(/^"|"$/g, '').trim();

const currentSegment = () => window.analytics as unknown as SegmentClient | undefined;

/**
 * Runs `fn` against the analytics.js instance present once the SDK is ready. Falls back to
 * calling immediately when `ready` is not a function (unit tests replace the snippet with a
 * plain mock; in the browser the snippet always provides `ready`).
 */
const withSegment = (fn: (segment: SegmentClient) => void) => {
  const stub = currentSegment();
  if (!stub) {
    return;
  }

  const invoke = () => {
    const segment = currentSegment();
    if (segment) {
      fn(segment);
    }
  };

  if (typeof stub.ready === 'function') {
    stub.ready(invoke);
    return;
  }

  invoke();
};

const currentAnonymousId = (segment: SegmentClient) => (
  segment.user?.()?.anonymousId?.() || undefined
);

/**
 * Segment `alias(to, from, ...)`. analytics.js (analytics-next) does NOT default `previousId`
 * when `from` is omitted, so a bare `alias(to)` produces an alias with no previous id. When
 * only `to` is given, the current anonymous id is used as `previousId` once the SDK is loaded;
 * a self-alias (both ids equal) is skipped because it carries no information.
 */
export const alias = (...args: unknown[]) => {
  const [to] = args;
  const onlyToGiven = args.length === 1 && typeof to === 'string';

  withSegment(segment => {
    const previousId = onlyToGiven ? currentAnonymousId(segment) : undefined;
    if (previousId === to) {
      return;
    }
    if (previousId) {
      segment.alias?.(to, previousId);
      return;
    }
    segment.alias?.(...args);
  });
};

let lastSegmentUserId: string | undefined;

/**
 * Ties the current anonymous id to the signed-in user in Segment: `alias(userId, anonymousId)`
 * then `identify(userId, traits)` — alias first so the anonymous history is merged before the
 * profile is created, as Segment recommends. Deduped per page load: repeated calls for the same
 * user id are no-ops until `clearSegmentIdentity()`/`resetSegmentIdentity()`.
 */
export const identifySegmentUser = (userId?: string, traits?: AnalyticsProperties) => {
  if (!userId || lastSegmentUserId === userId) {
    return;
  }
  if (!currentSegment()) {
    // No snippet on the page: do not burn the dedupe flag so a later effect run can retry.
    return;
  }
  lastSegmentUserId = userId;

  withSegment(segment => {
    const anonymousId = currentAnonymousId(segment);
    if (anonymousId && anonymousId !== userId) {
      segment.alias?.(userId, anonymousId);
    }
    segment.identify?.(userId, traits ?? {});
  });
};

let lastSegmentGroupId: string | undefined;

/**
 * Associates the current Segment user (or anonymous visitor) with the selected account via
 * `group(accountId, traits)`, mirroring the PostHog `group('account', ...)` call so account-level
 * attribution exists in Segment/Unify too. Deduped per account id per page load.
 */
export const identifySegmentAccount = (
  accountId?: number | string,
  traits?: AnalyticsProperties,
) => {
  if (accountId === undefined || accountId === null || accountId === '') {
    return;
  }
  const groupId = String(accountId);
  if (lastSegmentGroupId === groupId || !currentSegment()) {
    return;
  }
  lastSegmentGroupId = groupId;

  withSegment(segment => {
    segment.group?.(groupId, traits ?? {});
  });
};

/**
 * Forgets the in-memory dedupe only. Used whenever the user leaves the store — including a
 * transient token-refresh failure — so a subsequent sign-in identifies again. Deliberately
 * does NOT call `analytics.reset()`: that would rotate the anonymous id mid-session.
 */
export const clearSegmentIdentity = () => {
  lastSegmentUserId = undefined;
  lastSegmentGroupId = undefined;
};

/**
 * Explicit sign-out: forget the dedupe AND wipe Segment's stored user id + anonymous id so the
 * next visitor on this browser starts a fresh anonymous chain.
 *
 * Best-effort: `/logout` is a fresh page load, so `window.analytics` is usually still the
 * snippet stub and `reset` is only queued; it takes effect if analytics.js loads before the
 * Keycloak redirect wins. Deferring through `ready()` would be worse (the redirect kills the
 * page), so the call is issued synchronously.
 */
export const resetSegmentIdentity = () => {
  clearSegmentIdentity();
  currentSegment()?.reset?.();
};
